'use client';

import { useCallback, useRef, useState } from 'react';
import { PAGE_SIZE, dedupeById } from '../../../lib/document-hub-utils';
import { HubItem, Section } from '../../../interfaces';

interface DocumentHubDataHook {
  itemsBySection: Record<string, HubItem[]>;
  loadingBySection: Record<string, boolean>;
  maxVisibleBySection: Record<string, number>;
  noMoreBySection: Record<string, boolean>;
  setMaxVisibleBySection: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  fetchSlice: (section: Section, start: number, end: number) => Promise<HubItem[]>;
  ensureLoaded: (section: Section) => Promise<void>;
  loadMore: (section: Section) => Promise<void>;
  showAll: (section: Section) => Promise<void>;
  totalFor: (section: Section) => number;
  resetData: () => void;
}

export const useDocumentHubData = (
  sections: Section[],
  pageSlug: string | undefined,
  onDataFetched?: (files: any[]) => void,
): DocumentHubDataHook => {
  const [itemsBySection, setItemsBySection] = useState<Record<string, HubItem[]>>({});
  const [loadingBySection, setLoadingBySection] = useState<Record<string, boolean>>({});
  const [noMoreBySection, setNoMoreBySection] = useState<Record<string, boolean>>({});
  const loadedOnceRef = useRef<Set<string>>(new Set());
  const [maxVisibleBySection, setMaxVisibleBySection] = useState<Record<string, number>>({});
  const inflightRef = useRef<Map<string, number>>(new Map());

  const bumpInflight = useCallback((id: string, d: 1 | -1) => {
    const map = inflightRef.current;
    const next = (map.get(id) ?? 0) + d;
    map.set(id, Math.max(0, next));
    setLoadingBySection((p) => ({ ...p, [id]: next > 0 }));
  }, []);

  const totalFor = useCallback(
    (section: Section) =>
      Number.isFinite(section.count) ? Number(section.count) : (itemsBySection[section.id]?.length ?? 0),
    [itemsBySection],
  );

  const mapFilesToHubItems = (files: any[]): HubItem[] =>
    files.map((df) => {
      const fileUrl: string = df?.file?.url ?? '';
      return {
        id: String(df?._id ?? df?.file?.url ?? ''),
        title: df?.title ?? '',
        image: null,
        filename:
          df?.file?.originalFilename ?? (fileUrl ? decodeURIComponent(fileUrl.split('/').pop() || '') : undefined),
        fileUrl: fileUrl,
        tags: (df?.documentTags || []).map((t: any) => String(t?.name || '')).filter(Boolean),
      };
    });

  const fetchSlice = useCallback(
    async (section: Section, start: number, end: number): Promise<HubItem[]> => {
      if (!pageSlug) return [];
      const total = totalFor(section);
      const cappedStart = Math.min(start, total);
      const cappedEnd = Math.min(end, total);
      if (cappedEnd <= cappedStart) return [];

      bumpInflight(section.id, 1);
      try {
        const qs = new URLSearchParams({
          slug: pageSlug,
          panelKey: section.id,
          start: String(cappedStart),
          end: String(cappedEnd),
        });
        const res = await fetch(`/api/document-panels?${qs.toString()}`, {
          cache: 'no-store',
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const batch: any[] = json.files ?? [];
        onDataFetched?.(batch);

        if (batch.length < cappedEnd - cappedStart) {
          setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        }

        return mapFilesToHubItems(batch);
      } finally {
        bumpInflight(section.id, -1);
      }
    },
    [pageSlug, totalFor, onDataFetched, bumpInflight],
  );

  const ensureLoaded = useCallback(
    async (section: Section) => {
      if (loadedOnceRef.current.has(section.id)) return;
      const total = totalFor(section);
      const initialEnd = Math.min(PAGE_SIZE, total || PAGE_SIZE);
      const slice = await fetchSlice(section, 0, initialEnd);
      setItemsBySection((prev) => ({
        ...prev,
        [section.id]: dedupeById(slice),
      }));
      if (slice.length < initialEnd || (total && slice.length >= total)) {
        setNoMoreBySection((p) => ({
          ...p,
          [section.id]: total ? slice.length >= total : false,
        }));
      }
      loadedOnceRef.current.add(section.id);
    },
    [fetchSlice, totalFor],
  );

  const loadMore = useCallback(
    async (section: Section) => {
      if (noMoreBySection[section.id]) return;
      const current = itemsBySection[section.id] ?? [];
      const total = totalFor(section);
      if (total && current.length >= total) {
        setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        return;
      }
      const nextStart = current.length;
      const nextEnd = Math.min(nextStart + PAGE_SIZE, total || nextStart + PAGE_SIZE);

      setMaxVisibleBySection((p) => ({
        ...p,
        [section.id]: (p[section.id] ?? PAGE_SIZE) + PAGE_SIZE,
      }));

      const slice = await fetchSlice(section, nextStart, nextEnd);
      if (slice.length === 0) {
        setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        return;
      }
      setItemsBySection((p) => {
        const merged = dedupeById([...(p[section.id] ?? []), ...slice]);
        return { ...p, [section.id]: merged };
      });
      if (total && nextEnd >= total) setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
    },
    [itemsBySection, fetchSlice, totalFor, noMoreBySection],
  );

  const showAll = useCallback(
    async (section: Section) => {
      const current = itemsBySection[section.id] ?? [];
      const total = totalFor(section);

      setMaxVisibleBySection((p) => ({
        ...p,
        [section.id]: total || current.length,
      }));
      if ((total && current.length >= total) || noMoreBySection[section.id]) {
        setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
        return;
      }

      const slice = await fetchSlice(section, current.length, total || current.length);
      setItemsBySection((p) => {
        const merged = dedupeById([...(p[section.id] ?? []), ...slice]);
        return { ...p, [section.id]: merged };
      });
      setNoMoreBySection((p) => ({ ...p, [section.id]: true }));
    },
    [itemsBySection, fetchSlice, totalFor, noMoreBySection],
  );

  const resetData = useCallback(() => {
    setItemsBySection({});
    setLoadingBySection({});
    setNoMoreBySection({});
    loadedOnceRef.current = new Set();
    inflightRef.current = new Map();
    const init: Record<string, number> = {};
    sections.forEach((s) => (init[s.id] = PAGE_SIZE));
    setMaxVisibleBySection(init);
  }, [sections]);

  return {
    itemsBySection,
    loadingBySection,
    maxVisibleBySection,
    noMoreBySection,
    setMaxVisibleBySection,
    fetchSlice,
    ensureLoaded,
    loadMore,
    showAll,
    totalFor,
    resetData,
  };
};
